import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Button, Label, Spinner } from 'reactstrap';
import { RSelect } from '../../../components/Component';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { showToast } from '../../../utils/toast/toast';
import { useNavigate } from 'react-router-dom';
import { HttpStatus, newRoles } from '../../../utils/envConfig';
import { useDispatch } from 'react-redux';
import { fetchAgents } from '../../../redux/actions/user';

const ReassignLeadForm = ({ user, phone, onBack }) => {
  const [selectedLeadSource, setSelectedLeadSource] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activityNote, setActivityNote] = useState('');
  const [leadSources, setLeadSources] = useState([]);
  const [optAgents, setOptAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const axios = useAxiosPrivate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state?.user?.loggedInUser);
  const authAgent = useSelector((state) => state?.user?.loggedInAgent);
  const agents = useSelector((state) => state.user?.agents);

  if (!agents?.length) {
    dispatch(fetchAgents(axios))
  }

  useEffect(() => {
    fetchLeadSources();
    setupAgents();
  }, [agents]);

  const fetchLeadSources = async () => {
    try {
      const res = await axios.get('/leadSources/hotleads');
      let filteredLeadSources = res?.data?.data || [];

      if (authUser?.role === newRoles?.Agent) {
        filteredLeadSources = filteredLeadSources.filter(
          leadSource => !leadSource.name.startsWith("Call Center")
        );
      }

      setLeadSources(filteredLeadSources);
    } catch (err) {
      console.error('Error fetching lead sources:', err);
    }
  };

  const setupAgents = () => {
    if (!agents) return;

    const options = agents
      ?.filter((agent) => agent?.user?.active === true)
      ?.map((a) => ({
        value: a?.id,
        label: a?.user?.first_name + ' ' + a?.user?.last_name
      })) || [];

    setOptAgents(options);
  };

  const handleReassign = async () => {
    if (!selectedLeadSource) {
      showToast('Please select a lead source', 'error');
      return;
    }

    if (!authAgent && !selectedAgent) {
      showToast('Please select an agent', 'error');
      return;
    }

    setSaveLoading(true);

    try {
      const formData = {
        agent_id: authAgent ? (authAgent?.id ?? authAgent?._id).toString() : selectedAgent.toString(),
        user_ids: [user?.id ?? user?._id],
        notes: activityNote,
        leadSource: selectedLeadSource?.id,
        leadSourceName: selectedLeadSource?.name
      };

      const res = await axios.post("/usersAgents/assign", formData);

      if (res.status === HttpStatus.OK) {
        showToast('Lead assigned successfully', 'success');
        setTimeout(() => navigate(`/user-profile/${user?.id ?? user?._id}/user-profile-setting`), 500);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error assigning lead', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-4">
        <h4>{user.currentAgent ? 'Reassign Lead' : 'Assign Lead'}</h4>
        <p className="text-muted">
          {user.first_name} {user.last_name} - {phone}
        </p>
      </div>

      <Row className="gy-4">
        <Col sm="12">
          <div className="form-group">
            <Label className="form-label">Choose Lead Source *</Label>
            <RSelect
              closeMenuOnSelect={true}
              placeholder="Choose Lead Source"
              options={leadSources?.map((source) => ({
                ...source,
                label: source?.name,
                value: source?.name
              })) ?? []}
              onChange={(source) => setSelectedLeadSource(source)}
              value={selectedLeadSource}
              className="w-100"
            />
          </div>
        </Col>

        {authUser?.role && (
          authUser?.role !== newRoles?.Agent &&
          authUser?.role !== newRoles?.TeamLeader &&
          authUser?.role !== newRoles?.AnonymousAgent
        ) && (
            <Col sm="12">
              <div className="form-group">
                <Label className="form-label">Assign Agent *</Label>
                <RSelect
                  options={optAgents}
                  placeholder="Select Agent"
                  onChange={(selectedOption) => setSelectedAgent(selectedOption.value)}
                  value={optAgents.find(agent => agent.value === selectedAgent)}
                />
              </div>
            </Col>
          )}

        <Col sm="12">
          <div className="form-group">
            <label className="form-label">Note</label>
            <textarea
              name="notes"
              value={activityNote}
              onChange={(e) => setActivityNote(e.target.value)}
              className="form-control no-resize"
              placeholder="Add a note about this assignment..."
              rows="3"
            />
          </div>
        </Col>
      </Row>

      <div className="text-center mt-4">
        <Button color="light" onClick={onBack} className="me-2">
          Back
        </Button>
        <Button color="primary" onClick={handleReassign} disabled={saveLoading}>
          {saveLoading ? (
            <Spinner style={{ width: '1rem', height: '1rem' }} />
          ) : (
            user.currentAgent ? 'Reassign Lead' : 'Assign Lead'
          )}
        </Button>
      </div>
    </div>
  );
};

export default ReassignLeadForm;